import React, {useState, useEffect, useCallback} from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Tooltip,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LanguageIcon from '@mui/icons-material/Language';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import ReplayIcon from '@mui/icons-material/Replay';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ScienceIcon from '@mui/icons-material/Science';
import {useQuery, useMutation} from '@apollo/client';
import {ref, getDownloadURL} from 'firebase/storage';
import {GET_SOURCE_ITEM, GET_SOURCE_ITEM_DEPENDENCIES} from '../graphql/queries';
import {CLASSIFY_SOURCE_ITEMS, EXTRACT_SOURCE_ITEMS, OVERRIDE_SOURCE_ITEM_CLASSIFICATION, ADD_EVAL_TEST_CASE} from '../graphql/mutations';
import {storage} from '../config/firebase';
import {colors} from '../theme/colors';
import {useOrg} from '../contexts/OrgContext';
import {useToast} from '../contexts/ToastContext';

interface SourceItemDrawerProps {
  open: boolean;
  sourceItemId: string | null;
  onClose: () => void;
}

function sourceTypeIcon(type: string) {
  const sx = {fontSize: 20};
  switch (type) {
    case 'pdf': return <PictureAsPdfIcon sx={{...sx, color: '#EF4444'}} />;
    case 'url':
    case 'html': return <LanguageIcon sx={{...sx, color: '#3B82F6'}} />;
    case 'image': return <ImageIcon sx={{...sx, color: '#8B5CF6'}} />;
    default: return <DescriptionIcon sx={{...sx, color: '#6B7280'}} />;
  }
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Lightweight markdown → HTML for rendering page content preview.
 * Handles headings, bold, italic, links, lists, code blocks, and paragraphs.
 */
function simpleMarkdownToHtml(md: string): string {
  let html = md
    // Escape HTML entities (prevent XSS from extracted content)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Code blocks (``` ... ```)
    .replace(/```[\s\S]*?```/g, (match) => {
      const code = match.slice(3, -3).replace(/^\w*\n/, '');
      return `<pre><code>${code}</code></pre>`;
    })
    // Headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
    // Line breaks → paragraphs
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  return `<p>${html}</p>`;
}

function buildMarkdown(item: any, deps: any): string {
  const lines: string[] = [];
  lines.push(`# ${item.title}`);
  lines.push('');
  lines.push(`- **Type:** ${item.type.toUpperCase()}`);
  if (item.mimeType) lines.push(`- **MIME Type:** ${item.mimeType}`);
  if (item.sizeBytes) lines.push(`- **Size:** ${formatFileSize(item.sizeBytes)}`);
  lines.push(`- **Extraction:** ${item.extractionStatus}`);
  lines.push(`- **Review:** ${item.reviewStatus || 'UNREVIEWED'}`);
  const meta = item.extractionMetadata;
  if (meta?.documentType) lines.push(`- **Doc Type:** ${meta.documentType}`);
  if (meta?.audience) lines.push(`- **Audience:** ${meta.audience}`);
  lines.push(`- **Added:** ${new Date(item.createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}`);
  lines.push(`- **Updated:** ${new Date(item.updatedAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}`);
  if (item.sourceUrl) lines.push(`- **Source URL:** ${item.sourceUrl}`);
  if (meta?.topics?.length > 0) { lines.push(''); lines.push(`**Topics:** ${meta.topics.join(', ')}`); }
  if (meta?.entities?.length > 0) { lines.push(''); lines.push(`**Entities:** ${meta.entities.join(', ')}`); }
  const summaryText = item.summary || meta?.summary;
  if (summaryText) { lines.push(''); lines.push('## Summary'); lines.push(''); lines.push(summaryText); }
  if (item.extractedText) { lines.push(''); lines.push('## Extracted Text'); lines.push(''); lines.push(item.extractedText); }
  const hasDeps = deps && (deps.groupMemberships?.length > 0 || deps.citations?.length > 0 || deps.suggestions?.length > 0);
  if (hasDeps) {
    lines.push(''); lines.push('## Dependencies');
    if (deps.groupMemberships?.length > 0) { lines.push(''); lines.push('**Groups:**'); deps.groupMemberships.forEach((g: any) => lines.push(`- ${g.groupName}`)); }
    if (deps.citations?.length > 0) { lines.push(''); lines.push(`**Citations:** ${deps.citations.length} guide citation(s)`); }
    if (deps.suggestions?.length > 0) { lines.push(''); lines.push('**Suggestions:**'); deps.suggestions.forEach((s: any) => lines.push(`- ${s.suggestionName}`)); }
  }
  return lines.join('\n');
}

// Shared card style
const cardSx = {
  borderRadius: '12px',
  border: '1px solid #F0F0F0',
  bgcolor: '#fff',
  overflow: 'hidden',
};

const TOPIC_CATEGORIES = [
  'Health & Safety',
  'Curriculum & Instruction',
  'Enrollment & Registration',
  'Special Education',
  'Communications',
  'Finance & Budget',
  'Governance & Board',
  'Facilities & Operations',
  'Technology',
  'Human Resources & Employment',
  'Legal & Compliance',
  'Equity & Civil Rights',
  'Family & Community Engagement',
  'Transportation',
  'Food & Nutrition Services',
  'Emergency Management',
  'Procurement & Contracts',
  'Student Rights & Discipline',
  'Other',
];

const AUDIENCE_OPTIONS = ['External', 'Internal', 'Both'];

// Section label
function SectionLabel({children}: {children: React.ReactNode}) {
  return (
    <Typography sx={{fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1}}>
      {children}
    </Typography>
  );
}

// Inline metadata row
function MetaRow({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5}}>
      <Typography sx={{fontSize: 12, color: '#9CA3AF'}}>{label}</Typography>
      <Typography sx={{fontSize: 12, color: '#374151', textAlign: 'right'}}>{children}</Typography>
    </Box>
  );
}

// Pipeline step dot
function PipelineDot({status}: {status: 'completed' | 'processing' | 'failed' | 'pending'}) {
  const colorMap = {completed: '#10B981', processing: '#3B82F6', failed: '#EF4444', pending: '#D1D5DB'};
  if (status === 'processing') return <CircularProgress size={10} sx={{color: colorMap.processing}} />;
  return <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: colorMap[status], flexShrink: 0}} />;
}

export default function SourceItemDrawer({open, sourceItemId, onClose}: SourceItemDrawerProps) {
  const [textExpanded, setTextExpanded] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileInfoOpen, setFileInfoOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [evalGuideable, setEvalGuideable] = useState(true);
  const [evalTopic, setEvalTopic] = useState('Other');
  const [evalAudience, setEvalAudience] = useState('External');
  const {activeOrg} = useOrg();
  const {showToast} = useToast();

  const [classifySourceItems] = useMutation(CLASSIFY_SOURCE_ITEMS);
  const [extractSourceItems] = useMutation(EXTRACT_SOURCE_ITEMS);
  const [overrideClassification] = useMutation(OVERRIDE_SOURCE_ITEM_CLASSIFICATION);
  const [addEvalTestCase] = useMutation(ADD_EVAL_TEST_CASE);

  const {data, loading, error, startPolling, stopPolling, refetch} = useQuery(GET_SOURCE_ITEM, {
    variables: {id: sourceItemId},
    skip: !sourceItemId,
  });

  const item = data?.sourceItem;
  const meta = item?.extractionMetadata;
  const isTerminal = !item || item.extractionStatus === 'COMPLETED' || item.extractionStatus === 'FAILED';

  useEffect(() => {
    setDownloadUrl(null);
    if (item?.storagePath) {
      getDownloadURL(ref(storage, item.storagePath)).then(setDownloadUrl).catch(() => {});
    }
  }, [item?.storagePath]);

  useEffect(() => {
    if (sourceItemId && !isTerminal) { startPolling(3000); } else { stopPolling(); }
    return () => stopPolling();
  }, [sourceItemId, isTerminal, startPolling, stopPolling]);

  const {data: depsData} = useQuery(GET_SOURCE_ITEM_DEPENDENCIES, {
    variables: {sourceItemId},
    skip: !sourceItemId,
  });
  const deps = depsData?.sourceItemDependencies;
  const hasDeps = deps && (deps.groupMemberships.length > 0 || deps.citations.length > 0 || deps.suggestions.length > 0);

  const handleCopyMarkdown = useCallback(() => {
    if (!item) return;
    const md = buildMarkdown(item, deps);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(md).then(() => { showToast({message: 'Copied as Markdown', severity: 'success'}); });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = md; textarea.style.position = 'fixed'; textarea.style.opacity = '0';
      document.body.appendChild(textarea); textarea.select(); document.execCommand('copy'); document.body.removeChild(textarea);
      showToast({message: 'Copied as Markdown', severity: 'success'});
    }
  }, [item, deps, showToast]);

  // Derived analysis state (moved before callbacks that reference it)
  const override = item?.classificationOverride;
  const analysis = item?.analysis;
  const wave = item?.classificationWave2 || item?.classificationWave1;
  const isGuideable = override ? override.isProcess : analysis ? analysis.guideable : wave?.is_process;
  const topic = analysis?.topic;
  const audience = analysis?.audience;
  const guideTitle = analysis?.guide_title;
  const summary = analysis?.summary || item?.summary;
  const analysisFlags = analysis?.flags;

  const handleOpenEvalDialog = useCallback(() => {
    if (analysis) {
      setEvalGuideable(analysis.guideable ?? true);
      setEvalTopic(analysis.topic || 'Other');
      setEvalAudience(analysis.audience || 'External');
    }
    setEvalDialogOpen(true);
    setMenuAnchor(null);
  }, [analysis]);

  const handleSubmitEvalCase = useCallback(async () => {
    if (!item) return;
    try {
      await addEvalTestCase({
        variables: {
          orgId: activeOrg?.id,
          sourceItemId: item.id,
          expected: {
            guideable: evalGuideable,
            topic: evalTopic,
            audience: evalAudience,
          },
        },
      });
      showToast({message: 'Saved as eval test case', severity: 'success'});
      setEvalDialogOpen(false);
    } catch (err: any) {
      showToast({message: `Failed to save: ${err.message}`, severity: 'error'});
    }
  }, [item, addEvalTestCase, evalGuideable, evalTopic, evalAudience, showToast]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{sx: {width: 480, maxWidth: '100vw', bgcolor: '#FAFAFA', borderLeft: 'none'}}}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: '#fff',
        borderBottom: '1px solid #F0F0F0',
      }}>
        <IconButton size="small" onClick={onClose} sx={{color: '#9CA3AF', '&:hover': {bgcolor: '#F3F4F6'}}}>
          <CloseIcon sx={{fontSize: 20}} />
        </IconButton>
        {item && sourceTypeIcon(item.type)}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography sx={{fontSize: 15, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {item?.title ?? 'Source Item'}
          </Typography>
          {item && (
            <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
              {item.type.toUpperCase()} {item.sizeBytes ? `\u00b7 ${formatFileSize(item.sizeBytes)}` : ''}
            </Typography>
          )}
        </Box>
        {item && (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{color: '#9CA3AF', '&:hover': {color: '#374151', bgcolor: '#F3F4F6'}}}>
              <MoreVertIcon sx={{fontSize: 20}} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
              transformOrigin={{vertical: 'top', horizontal: 'right'}}
              slotProps={{paper: {sx: {borderRadius: '10px', minWidth: 180, boxShadow: '0 4px 16px rgba(0,0,0,0.1)'}}}}
            >
              <MenuItem onClick={() => { handleCopyMarkdown(); setMenuAnchor(null); }}>
                <ListItemIcon><ContentCopyIcon sx={{fontSize: 16}} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{fontSize: 13}}>Copy as Markdown</ListItemText>
              </MenuItem>
              {downloadUrl && (
                <MenuItem component="a" href={downloadUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuAnchor(null)}>
                  <ListItemIcon><DownloadIcon sx={{fontSize: 16}} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{fontSize: 13}}>Download</ListItemText>
                </MenuItem>
              )}
              {item.sourceUrl && (
                <MenuItem component="a" href={item.sourceUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuAnchor(null)}>
                  <ListItemIcon><OpenInNewIcon sx={{fontSize: 16}} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{fontSize: 13}}>Open source</ListItemText>
                </MenuItem>
              )}
              {analysis && (
                <MenuItem onClick={handleOpenEvalDialog}>
                  <ListItemIcon><ScienceIcon sx={{fontSize: 16}} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{fontSize: 13}}>Flag as eval case</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {/* Content */}
      <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress size={28} sx={{color: '#9CA3AF'}} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{mb: 2, borderRadius: '12px'}}>Error loading item: {error.message}</Alert>
        )}

        {item && (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>

            {/* Hero card: Guideable status + summary */}
            <Box sx={{...cardSx, p: 2}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: summary || guideTitle ? 1.5 : 0, flexWrap: 'wrap'}}>
                {isGuideable !== undefined && isGuideable !== null && (
                  <Chip
                    label={isGuideable ? 'Guideable' : 'Not guideable'}
                    size="small"
                    sx={{
                      fontWeight: 600, fontSize: 12, height: 26,
                      borderRadius: '8px',
                      bgcolor: isGuideable ? '#ECFDF5' : '#F3F4F6',
                      color: isGuideable ? '#065F46' : '#6B7280',
                      border: `1px solid ${isGuideable ? '#A7F3D0' : '#E5E7EB'}`,
                    }}
                  />
                )}
                {topic && (
                  <Chip
                    label={topic}
                    size="small"
                    sx={{
                      fontSize: 11, height: 24, borderRadius: '8px',
                      bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE',
                    }}
                  />
                )}
                {audience && (
                  <Chip
                    label={audience}
                    size="small"
                    sx={{
                      fontSize: 11, height: 24, borderRadius: '8px',
                      bgcolor: audience === 'External' ? '#FEF3C7' : audience === 'Internal' ? '#F3F4F6' : '#EDE9FE',
                      color: audience === 'External' ? '#92400E' : audience === 'Internal' ? '#374151' : '#5B21B6',
                      border: `1px solid ${audience === 'External' ? '#FDE68A' : audience === 'Internal' ? '#E5E7EB' : '#DDD6FE'}`,
                    }}
                  />
                )}
                <Button
                  size="small"
                  onClick={async () => {
                    await overrideClassification({variables: {id: item.id, isProcess: !isGuideable}});
                    refetch();
                    showToast({message: isGuideable ? 'Marked as not guideable' : 'Marked as guideable', severity: 'success'});
                  }}
                  sx={{
                    textTransform: 'none', fontSize: 11, minWidth: 0, py: 0.25, px: 1,
                    ml: 'auto', color: '#6B7280', borderRadius: '6px',
                    '&:hover': {bgcolor: '#F3F4F6'},
                  }}
                >
                  Mark {isGuideable ? 'not guideable' : 'guideable'}
                </Button>
              </Box>
              {/* Attribution line */}
              {override ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
                  <PersonIcon sx={{fontSize: 13, color: '#9CA3AF'}} />
                  <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                    Overridden by {override.setBy || 'user'}
                  </Typography>
                </Box>
              ) : analysis ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
                  <AutoFixHighIcon sx={{fontSize: 13, color: '#9CA3AF'}} />
                  <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                    Set by AI ({analysis.model || 'model'})
                  </Typography>
                </Box>
              ) : null}
              {guideTitle && (
                <Typography sx={{fontSize: 14, fontWeight: 600, color: '#111827', mb: 0.5, lineHeight: 1.4}}>
                  {guideTitle}
                </Typography>
              )}
              {summary && (
                <Typography sx={{fontSize: 13, color: '#4B5563', lineHeight: 1.6}}>
                  {summary}
                </Typography>
              )}
              {/* "Why?" collapsible callout */}
              {analysis?.guideable_rationale && (
                <>
                  <Box
                    onClick={() => setWhyOpen(!whyOpen)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, cursor: 'pointer',
                      userSelect: 'none', '&:hover': {opacity: 0.8},
                    }}
                  >
                    <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>Why?</Typography>
                    <KeyboardArrowDownIcon sx={{fontSize: 16, color: '#9CA3AF', transform: whyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />
                  </Box>
                  <Collapse in={whyOpen}>
                    <Box sx={{mt: 0.75, p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F0F0F0'}}>
                      <Typography sx={{fontSize: 12, color: '#4B5563', lineHeight: 1.5, mb: 1}}>
                        {analysis.guideable_rationale}
                      </Typography>
                      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center'}}>
                        {wave && (
                          <Chip label={`Wave ${item.classificationWave2 ? '2' : '1'}`} size="small"
                            sx={{fontSize: 10, height: 20, borderRadius: '6px', bgcolor: '#EFF6FF', color: '#1D4ED8'}} />
                        )}
                        {analysis.model && (
                          <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                            Model: {analysis.model}
                          </Typography>
                        )}
                        {analysis.analyzedAt && (
                          <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                            {new Date(analysis.analyzedAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </>
              )}
              {analysisFlags && analysisFlags.length > 0 && (
                <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5}}>
                  {analysisFlags.map((flag: string, i: number) => (
                    <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 0.75}}>
                      <FlagIcon sx={{fontSize: 13, color: '#F59E0B', mt: '2px', flexShrink: 0}} />
                      <Typography sx={{fontSize: 12, color: '#92400E', lineHeight: 1.4}}>
                        {flag}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Source & Discovery */}
            {(item.sourceUrl || item.linkedFromPages?.length > 0) && (
              <Box sx={{...cardSx, p: 2}}>
                <SectionLabel>Source</SectionLabel>
                {item.sourceUrl && (
                  <Link
                    href={item.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: 12, color: '#3B82F6', display: 'flex', alignItems: 'center', gap: 0.5,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textDecoration: 'none', '&:hover': {textDecoration: 'underline'},
                    }}
                  >
                    <LanguageIcon sx={{fontSize: 14, flexShrink: 0}} />
                    <Box component="span" sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{item.sourceUrl}</Box>
                    <OpenInNewIcon sx={{fontSize: 11, flexShrink: 0, opacity: 0.6}} />
                  </Link>
                )}
                {item.linkedFromPages?.length > 0 && (
                  <Box sx={{mt: item.sourceUrl ? 1.5 : 0}}>
                    <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.75}}>Found on</Typography>
                    {item.linkedFromPages.map((page: any, i: number) => (
                      <Box key={i} sx={{mb: 0.75}}>
                        {page.title && (
                          <Typography sx={{fontSize: 13, fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {page.title}
                          </Typography>
                        )}
                        <Link
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 0.5,
                            textDecoration: 'none', '&:hover': {color: '#3B82F6'},
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          <Box component="span" sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{page.url}</Box>
                          <OpenInNewIcon sx={{fontSize: 10, flexShrink: 0}} />
                        </Link>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Pipeline card */}
            <Box sx={{...cardSx, p: 2}}>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5}}>
                <SectionLabel>Pipeline</SectionLabel>
                <Box sx={{display: 'flex', gap: 0.5}}>
                  <Button
                    size="small"
                    startIcon={<ReplayIcon sx={{fontSize: 12}} />}
                    onClick={async () => {
                      if (!activeOrg?.id) return;
                      await classifySourceItems({variables: {orgId: activeOrg.id, sourceItemIds: [item.id]}});
                      refetch();
                    }}
                    sx={{textTransform: 'none', fontSize: 11, minWidth: 0, py: 0, px: 1, color: '#6B7280', borderRadius: '6px', '&:hover': {bgcolor: '#F3F4F6'}}}
                  >
                    Re-analyze
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ReplayIcon sx={{fontSize: 12}} />}
                    onClick={async () => {
                      if (!activeOrg?.id) return;
                      await extractSourceItems({variables: {orgId: activeOrg.id, sourceItemIds: [item.id]}});
                      refetch();
                    }}
                    sx={{textTransform: 'none', fontSize: 11, minWidth: 0, py: 0, px: 1, color: '#6B7280', borderRadius: '6px', '&:hover': {bgcolor: '#F3F4F6'}}}
                  >
                    Re-extract
                  </Button>
                </Box>
              </Box>

              {/* Analysis step */}
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75}}>
                <PipelineDot status={
                  item.analysisStatus === 'completed' || item.classificationStatus === 'completed' ? 'completed'
                  : item.analysisStatus === 'processing' || item.classificationStatus === 'processing' ? 'processing'
                  : item.analysisStatus === 'failed' || item.classificationStatus === 'failed' ? 'failed'
                  : 'pending'
                } />
                <Typography sx={{fontSize: 13, color: '#374151', flex: 1}}>Analysis</Typography>
                <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                  {item.analysisStatus === 'completed' || item.classificationStatus === 'completed' ? 'Done'
                  : item.analysisStatus === 'processing' || item.classificationStatus === 'processing' ? 'Running...'
                  : item.analysisStatus === 'failed' || item.classificationStatus === 'failed' ? 'Failed'
                  : 'Pending'}
                </Typography>
              </Box>

              {/* Extraction step */}
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75}}>
                <PipelineDot status={
                  item.extractionStatus === 'COMPLETED' ? 'completed'
                  : item.extractionStatus === 'PROCESSING' ? 'processing'
                  : item.extractionStatus === 'FAILED' ? 'failed'
                  : 'pending'
                } />
                <Typography sx={{fontSize: 13, color: '#374151', flex: 1}}>Extraction</Typography>
                <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                  {(() => {
                    if (item.extractionStatus === 'COMPLETED') return 'Done';
                    if (item.extractionStatus === 'PROCESSING') return 'Running...';
                    if (item.extractionStatus === 'FAILED') return 'Failed';
                    const guideable = item.classificationOverride?.isProcess ?? item.analysis?.guideable ?? (item.classificationWave2 || item.classificationWave1)?.is_process;
                    if (guideable === false) return 'Skipped';
                    return 'Pending';
                  })()}
                </Typography>
              </Box>

              {/* Grouping step */}
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75}}>
                <PipelineDot status={deps?.groupMemberships?.length > 0 ? 'completed' : 'pending'} />
                <Typography sx={{fontSize: 13, color: '#374151', flex: 1}}>Grouping</Typography>
                <Typography sx={{fontSize: 11, color: '#9CA3AF'}}>
                  {deps?.groupMemberships?.length > 0 ? `${deps.groupMemberships.length} group(s)` : 'None'}
                </Typography>
              </Box>
            </Box>

            {/* File Info (collapsed) */}
            <Box sx={cardSx}>
              <Box
                onClick={() => setFileInfoOpen(!fileInfoOpen)}
                sx={{
                  px: 2, py: 1.25,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', '&:hover': {bgcolor: '#FAFAFA'},
                  userSelect: 'none',
                }}
              >
                <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>File Info</Typography>
                <KeyboardArrowDownIcon sx={{fontSize: 18, color: '#9CA3AF', transform: fileInfoOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />
              </Box>
              <Collapse in={fileInfoOpen}>
                <Box sx={{px: 2, pb: 1.5}}>
                  <MetaRow label="Type">{item.type.toUpperCase()}</MetaRow>
                  {item.mimeType && <MetaRow label="MIME">{item.mimeType}</MetaRow>}
                  <MetaRow label="Size">{formatFileSize(item.sizeBytes)}</MetaRow>
                  <MetaRow label="Added">{new Date(item.createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}</MetaRow>
                  <MetaRow label="Updated">{new Date(item.updatedAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}</MetaRow>
                  {meta?.documentType && <MetaRow label="Doc Type"><span style={{textTransform: 'capitalize'}}>{meta.documentType}</span></MetaRow>}
                  {meta?.audience && <MetaRow label="Audience">{meta.audience}</MetaRow>}
                </Box>
              </Collapse>
            </Box>

            {/* Topics & Entities */}
            {meta && (meta.topics?.length > 0 || meta.entities?.length > 0) && (
              <Box sx={{...cardSx, p: 2}}>
                {meta.topics?.length > 0 && (
                  <Box sx={{mb: meta.entities?.length > 0 ? 1.5 : 0}}>
                    <SectionLabel>Topics</SectionLabel>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                      {meta.topics.map((t: string) => (
                        <Chip key={t} label={t} size="small" sx={{fontSize: 11, height: 22, borderRadius: '6px', bgcolor: '#F3F4F6', color: '#374151'}} />
                      ))}
                    </Box>
                  </Box>
                )}
                {meta.entities?.length > 0 && (
                  <Box>
                    <SectionLabel>Entities</SectionLabel>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                      {meta.entities.map((e: string) => (
                        <Chip key={e} label={e} size="small" sx={{fontSize: 11, height: 22, borderRadius: '6px', bgcolor: '#EFF6FF', color: '#1D4ED8'}} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Page Content (rendered markdown for HTML source items) */}
            {item.type === 'html' && item.extractedText && (
              <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{...cardSx, '&:before': {display: 'none'}, '& .MuiAccordionSummary-root': {minHeight: 40}}}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{fontSize: 18, color: '#9CA3AF'}} />} sx={{px: 2, py: 0}}>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>Page Content</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{px: 2, pt: 0, pb: 2}}>
                  <Box
                    sx={{
                      bgcolor: '#FAFAFA', borderRadius: '8px', p: 2,
                      fontSize: 13, lineHeight: 1.7, color: '#374151',
                      maxHeight: textExpanded ? 'none' : 400, overflow: 'hidden',
                      '& h1': {fontSize: 18, fontWeight: 700, color: '#111827', mt: 0, mb: 1},
                      '& h2': {fontSize: 16, fontWeight: 600, color: '#111827', mt: 2, mb: 0.75},
                      '& h3': {fontSize: 14, fontWeight: 600, color: '#374151', mt: 1.5, mb: 0.5},
                      '& p': {mb: 1},
                      '& ul, & ol': {pl: 2.5, mb: 1},
                      '& li': {mb: 0.25},
                      '& a': {color: '#3B82F6', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}},
                      '& code': {bgcolor: '#F3F4F6', px: 0.5, py: 0.25, borderRadius: '4px', fontSize: 12, fontFamily: '"SF Mono", "Fira Code", monospace'},
                      '& pre': {bgcolor: '#F3F4F6', p: 1.5, borderRadius: '8px', overflow: 'auto', fontSize: 12},
                      '& blockquote': {borderLeft: '3px solid #E5E7EB', pl: 1.5, color: '#6B7280', ml: 0},
                      '& table': {borderCollapse: 'collapse', width: '100%', fontSize: 12, mb: 1},
                      '& th, & td': {border: '1px solid #E5E7EB', px: 1, py: 0.5, textAlign: 'left'},
                      '& th': {bgcolor: '#F9FAFB', fontWeight: 600},
                    }}
                    dangerouslySetInnerHTML={{__html: simpleMarkdownToHtml(item.extractedText)}}
                  />
                  {item.extractedText.length > 2000 && (
                    <Typography
                      component="button"
                      onClick={() => setTextExpanded(!textExpanded)}
                      sx={{
                        display: 'block', mx: 'auto', mt: 1,
                        fontSize: 12, color: '#3B82F6', fontWeight: 500,
                        background: 'none', border: 'none', cursor: 'pointer',
                        '&:hover': {textDecoration: 'underline'},
                      }}
                    >
                      {textExpanded ? 'Show less' : 'Show all'}
                    </Typography>
                  )}
                  {item.sourceUrl && (
                    <Link
                      href={item.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: 11, color: '#3B82F6', mt: 1, display: 'flex', alignItems: 'center', gap: 0.5,
                        textDecoration: 'none', '&:hover': {textDecoration: 'underline'},
                      }}
                    >
                      Open original page <OpenInNewIcon sx={{fontSize: 10}} />
                    </Link>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Extracted text */}
            {item.extractionStatus === 'COMPLETED' && item.extractedText && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{...cardSx, '&:before': {display: 'none'}, '& .MuiAccordionSummary-root': {minHeight: 40}}}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{fontSize: 18, color: '#9CA3AF'}} />} sx={{px: 2, py: 0}}>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>
                    {item.type === 'html' ? 'Extracted Markdown' : 'Extracted Text'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{px: 2, pt: 0, pb: 2}}>
                  <Box
                    sx={{
                      bgcolor: '#F9FAFB', borderRadius: '8px', p: 1.5,
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      fontSize: 11, lineHeight: 1.6, color: '#374151',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: textExpanded ? 'none' : 300, overflow: 'hidden',
                    }}
                  >
                    {item.extractedText}
                  </Box>
                  {item.extractedText.length > 1500 && (
                    <Typography
                      component="button"
                      onClick={() => setTextExpanded(!textExpanded)}
                      sx={{
                        display: 'block', mx: 'auto', mt: 1,
                        fontSize: 12, color: '#3B82F6', fontWeight: 500,
                        background: 'none', border: 'none', cursor: 'pointer',
                        '&:hover': {textDecoration: 'underline'},
                      }}
                    >
                      {textExpanded ? 'Show less' : 'Show all'}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Dependencies */}
            {hasDeps && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{...cardSx, '&:before': {display: 'none'}, '& .MuiAccordionSummary-root': {minHeight: 40}}}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{fontSize: 18, color: '#9CA3AF'}} />} sx={{px: 2, py: 0}}>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>Dependencies</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{px: 2, pt: 0, pb: 2}}>
                  {deps.groupMemberships.length > 0 && (
                    <Box sx={{mb: 1}}>
                      <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Groups</Typography>
                      {deps.groupMemberships.map((g: any) => (
                        <Typography key={g.groupId} sx={{fontSize: 12, color: '#374151', ml: 0.5}}>{g.groupName}</Typography>
                      ))}
                    </Box>
                  )}
                  {deps.citations.length > 0 && (
                    <Box sx={{mb: 1}}>
                      <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Citations</Typography>
                      <Typography sx={{fontSize: 12, color: '#374151', ml: 0.5}}>{deps.citations.length} guide citation(s)</Typography>
                    </Box>
                  )}
                  {deps.suggestions.length > 0 && (
                    <Box>
                      <Typography sx={{fontSize: 11, color: '#9CA3AF', mb: 0.5}}>Suggestions</Typography>
                      {deps.suggestions.map((s: any) => (
                        <Typography key={s.suggestionId} sx={{fontSize: 12, color: '#374151', ml: 0.5}}>{s.suggestionName}</Typography>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

          </Box>
        )}
      </Box>
      {/* Eval test case dialog */}
      <Dialog open={evalDialogOpen} onClose={() => setEvalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>Flag as Eval Case</DialogTitle>
        <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important'}}>
          <Typography sx={{fontSize: 13, color: '#6B7280'}}>
            Set the expected (correct) classification for this item. This will be used to evaluate prompt accuracy.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={evalGuideable}
                onChange={(e) => setEvalGuideable(e.target.checked)}
                size="small"
              />
            }
            label={<Typography sx={{fontSize: 13}}>Guideable</Typography>}
          />
          <FormControl size="small" fullWidth>
            <InputLabel sx={{fontSize: 13}}>Topic</InputLabel>
            <Select
              native
              value={evalTopic}
              onChange={(e) => setEvalTopic(e.target.value as string)}
              label="Topic"
              sx={{fontSize: 13}}
            >
              {TOPIC_CATEGORIES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{fontSize: 13}}>Audience</InputLabel>
            <Select
              native
              value={evalAudience}
              onChange={(e) => setEvalAudience(e.target.value as string)}
              label="Audience"
              sx={{fontSize: 13}}
            >
              {AUDIENCE_OPTIONS.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={() => setEvalDialogOpen(false)} sx={{textTransform: 'none', fontSize: 13, color: '#6B7280'}}>
            Cancel
          </Button>
          <Button onClick={handleSubmitEvalCase} variant="contained" sx={{textTransform: 'none', fontSize: 13}}>
            Save Test Case
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
